/**
 * Shared KaTeX rendering.
 *
 * Used both by the math tokenizer — to decide whether a `$…$` / `$$…$$` span is
 * genuinely math — and by Cmarkdownmath to render it. Routing both through one
 * entry point guarantees the tokenizer only ever claims spans the renderer can
 * actually display: no broken-math `<code>` fallbacks, and no `$` silently eaten
 * out of ordinary prose, currency, or identifiers.
 *
 * Every option below is load-bearing for content authored by a model:
 *
 * - `strict: 'error'` turns KaTeX's soft warnings into rejections. The two that
 *   matter are `unknownSymbol` and `unicodeTextInMathMode`: an em-dash, an
 *   arrow, or a Japanese place name inside a candidate span is prose that a
 *   stray `$` reached across, and under the default `'warn'` KaTeX renders it
 *   happily as run-together italics. It is a filter, not cosmetics — the
 *   tokenizer reads this function's verdict.
 * - `trust: false` keeps `\href`, `\url`, and `\includegraphics` out of markup
 *   that is injected with `dangerouslySetInnerHTML`.
 * - `maxSize` clamps `\rule`-style length arguments so a single span cannot
 *   blow out the page, and `maxExpand` bounds macro expansion.
 */

import katex from 'katex';

/** KaTeX's `strict` option — the vendor's closed set of named levels. */
enum KatexStrictMode {
  Ignore = 'ignore',
  Warn = 'warn',
  Error = 'error',
}

const KATEX_ERROR_CLASS_NAME = 'katex-error';
/** Upper bound, in `em`, for any user-specified size. Unbounded by default. */
const MAX_USER_SPECIFIED_SIZE_EM = 50;
/** Macro expansions permitted per render. Matches KaTeX's own default. */
const MAX_MACRO_EXPANSIONS = 1000;

export interface RenderLatexInput {
  latex: string;
  displayMode: boolean;
}

/** Returns the rendered KaTeX HTML, or `null` when the input is not valid math. */
export function renderLatexToHtml({ latex, displayMode }: RenderLatexInput): string | null {
  try {
    const markup = katex.renderToString(latex, {
      displayMode,
      throwOnError: false,
      trust: false,
      strict: KatexStrictMode.Error,
      maxSize: MAX_USER_SPECIFIED_SIZE_EM,
      maxExpand: MAX_MACRO_EXPANSIONS,
    });

    return markup.includes(KATEX_ERROR_CLASS_NAME) ? null : markup;
  } catch {
    return null;
  }
}

/** True when KaTeX can render `latex` without error. */
export function isRenderableLatex(input: RenderLatexInput): boolean {
  return renderLatexToHtml(input) !== null;
}
